import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import Navbar from '@/components/Navbar';
import EditorialHero from '@/components/EditorialHero';
import ImageUpload from '@/components/ImageUpload';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { ArrowLeft, Save, Loader2, Eye } from 'lucide-react';

export default function AdminHomepageHero() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [seriesOptions, setSeriesOptions] = useState([]);
  const [form, setForm] = useState({
    enabled: true,
    series_id: '',
    label: 'Nova série',
    custom_synopsis: '',
    background_image: '',
    start_date: '',
    end_date: '',
    countdown_enabled: false,
    countdown_target_date: '',
    countdown_label: 'Próximo episódio em',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [previewKey, setPreviewKey] = useState(0);

  useEffect(() => {
    if (!user?.is_admin) {
      navigate('/');
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const [heroRes, seriesRes] = await Promise.all([
          api.get('/admin/homepage/hero'),
          api.get('/series').catch(() => ({ data: [] })),
        ]);
        if (cancelled) return;
        const cfg = heroRes.data?.config || {};
        setForm({
          enabled: cfg.enabled ?? true,
          series_id: cfg.series_id || '',
          label: cfg.label || 'Nova série',
          custom_synopsis: cfg.custom_synopsis || '',
          background_image: cfg.background_image || '',
          start_date: (cfg.start_date || '').slice(0, 10),
          end_date: (cfg.end_date || '').slice(0, 10),
          countdown_enabled: !!cfg.countdown_enabled,
          countdown_target_date: (cfg.countdown_target_date || '').slice(0, 16),
          countdown_label: cfg.countdown_label || 'Próximo episódio em',
        });
        setSeriesOptions(seriesRes.data || []);
      } catch (err) {
        toast.error('Erro ao carregar configuração do hero');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [user, navigate]);

  const handleSave = async (e) => {
    e?.preventDefault?.();
    if (form.enabled && !form.series_id) {
      toast.error('Escolhe uma série para o hero.');
      return;
    }
    if (form.countdown_enabled && !form.countdown_target_date) {
      toast.error('Define uma data-alvo para a contagem decrescente.');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        // Store dates as ISO — empty stays empty.
        start_date: form.start_date ? new Date(form.start_date + 'T00:00:00Z').toISOString() : '',
        end_date: form.end_date ? new Date(form.end_date + 'T23:59:59Z').toISOString() : '',
        countdown_target_date: form.countdown_target_date
          ? new Date(form.countdown_target_date).toISOString()
          : '',
      };
      await api.put('/admin/homepage/hero', payload);
      toast.success('Hero atualizado');
      setPreviewKey((k) => k + 1);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erro ao guardar');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-ruby" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="pt-24 pb-12 px-4 md:px-8 max-w-5xl mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <Button variant="ghost" onClick={() => navigate('/admin')} className="text-muted-foreground">
            <ArrowLeft className="w-4 h-4 mr-2" />Voltar
          </Button>
          <h1 className="font-brand text-2xl md:text-3xl font-bold text-foreground">
            Hero editorial da Homepage
          </h1>
        </div>

        <Card className="bg-surface border-border mb-6">
          <CardHeader>
            <CardTitle className="text-foreground">Configuração</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSave} className="space-y-6">
              {/* Enabled */}
              <div className="flex items-center justify-between p-4 rounded-lg bg-background/40 border border-border">
                <div>
                  <Label htmlFor="hero-enabled" className="text-foreground">Hero ativo</Label>
                  <p className="text-sm text-muted-foreground">Quando desligado, a homepage não mostra este destaque.</p>
                </div>
                <Switch
                  id="hero-enabled"
                  checked={form.enabled}
                  onCheckedChange={(v) => setForm({ ...form, enabled: v })}
                  data-testid="hero-enabled-switch"
                />
              </div>

              {/* Series selector */}
              <div className="space-y-2">
                <Label>Série em destaque *</Label>
                <Select
                  value={form.series_id}
                  onValueChange={(v) => setForm({ ...form, series_id: v })}
                >
                  <SelectTrigger className="bg-background border-border" data-testid="hero-series-select">
                    <SelectValue placeholder="Escolhe uma série" />
                  </SelectTrigger>
                  <SelectContent>
                    {seriesOptions.map((s) => (
                      <SelectItem key={s.id} value={s.id}>
                        {s.title} · {s.episode_count || 0} ep.
                      </SelectItem>
                    ))}
                    {/* Preserve foreign IDs (e.g. series in production but not in preview) */}
                    {form.series_id && !seriesOptions.some((s) => s.id === form.series_id) && (
                      <SelectItem value={form.series_id}>{form.series_id} (ID externo)</SelectItem>
                    )}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground font-mono break-all">
                  {form.series_id || '—'}
                </p>
              </div>

              {/* Label */}
              <div className="space-y-2">
                <Label htmlFor="hero-label">Etiqueta visível</Label>
                <Input
                  id="hero-label"
                  value={form.label}
                  onChange={(e) => setForm({ ...form, label: e.target.value.slice(0, 40) })}
                  placeholder='Ex.: "Nova série", "Em destaque"'
                  className="bg-background border-border"
                  data-testid="hero-label-input"
                />
              </div>

              {/* Custom synopsis */}
              <div className="space-y-2">
                <Label htmlFor="hero-synopsis">Texto promocional (opcional)</Label>
                <Textarea
                  id="hero-synopsis"
                  value={form.custom_synopsis}
                  onChange={(e) => setForm({ ...form, custom_synopsis: e.target.value.slice(0, 500) })}
                  placeholder="Deixa em branco para usar a sinopse original da série."
                  className="bg-background border-border min-h-[120px]"
                  data-testid="hero-synopsis-input"
                />
                <p className="text-xs text-muted-foreground text-right">{form.custom_synopsis.length}/500</p>
              </div>

              {/* Background image */}
              <div className="space-y-2">
                <Label>Imagem promocional (opcional)</Label>
                <ImageUpload
                  value={form.background_image}
                  onChange={(url) => setForm({ ...form, background_image: url || '' })}
                  label=""
                  aspectRatio="aspect-[16/9]"
                  recommendedSize="1920×1080 · 16:9"
                  hint="Deixa em branco para usar a capa da série."
                />
              </div>

              {/* Dates */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="hero-start">Data de início (opcional)</Label>
                  <Input
                    id="hero-start"
                    type="date"
                    value={form.start_date}
                    onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                    className="bg-background border-border"
                    data-testid="hero-start-date"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="hero-end">Data de fim (opcional)</Label>
                  <Input
                    id="hero-end"
                    type="date"
                    value={form.end_date}
                    onChange={(e) => setForm({ ...form, end_date: e.target.value })}
                    className="bg-background border-border"
                    data-testid="hero-end-date"
                  />
                </div>
              </div>

              {/* Countdown */}
              <div className="space-y-4 p-4 rounded-lg bg-background/40 border border-border">
                <div className="flex items-center justify-between">
                  <div>
                    <Label htmlFor="countdown-enabled" className="text-foreground">Contagem decrescente</Label>
                    <p className="text-sm text-muted-foreground">Mostra um contador em tempo real no hero até uma data-alvo (próximo episódio, estreia, etc.).</p>
                  </div>
                  <Switch
                    id="countdown-enabled"
                    checked={form.countdown_enabled}
                    onCheckedChange={(v) => setForm({ ...form, countdown_enabled: v })}
                    data-testid="countdown-enabled-switch"
                  />
                </div>
                {form.countdown_enabled && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="countdown-label">Texto antes do contador</Label>
                      <Input
                        id="countdown-label"
                        value={form.countdown_label}
                        onChange={(e) => setForm({ ...form, countdown_label: e.target.value.slice(0, 40) })}
                        placeholder='Ex.: "Próximo episódio em", "Estreia em"'
                        className="bg-background border-border"
                        data-testid="countdown-label-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="countdown-target">Data-alvo *</Label>
                      <Input
                        id="countdown-target"
                        type="datetime-local"
                        value={form.countdown_target_date}
                        onChange={(e) => setForm({ ...form, countdown_target_date: e.target.value })}
                        className="bg-background border-border"
                        data-testid="countdown-target-date"
                      />
                      <p className="text-xs text-muted-foreground">
                        Quando a data-alvo passar, o contador esconde-se automaticamente.
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="flex flex-wrap gap-3 pt-4 border-t border-border">
                <Button
                  type="submit"
                  disabled={saving}
                  className="bg-ruby hover:bg-ruby-light"
                  data-testid="hero-save-btn"
                >
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  Guardar alterações
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowPreview((v) => !v)}
                  className="border-border"
                  data-testid="hero-preview-btn"
                >
                  <Eye className="w-4 h-4 mr-2" />
                  {showPreview ? 'Ocultar pré-visualização' : 'Pré-visualizar'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        {showPreview && (
          <div>
            <h2 className="text-lg font-semibold text-foreground mb-3">Pré-visualização</h2>
            <div className="rounded-2xl border border-border overflow-hidden -mx-4 md:-mx-8">
              {/* Re-mount EditorialHero on preview toggles + save to force a fresh fetch */}
              <div key={previewKey} data-testid="hero-preview-wrapper">
                <EditorialHero />
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              A pré-visualização lê o estado guardado. Guarda antes para ver as últimas alterações.
            </p>
          </div>
        )}
      </main>
    </div>
  );
}
